import React from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import Pagination from "../../shared/Pagination";
import useClientPagination from "../../../hooks/useClientPagination";
import { NeedsMultipleSyncs } from "./AbsorptionVelocityPanel";
import { getCiReportAbsorptionVsBaseline } from "../../../api/competitiveIntelligence";
import { categoryLabel } from "../../../utils/ciFormat";
import ThinScroll from "../../shared/ThinScroll";
import { PanelTakeaway, fmtDate, fmtPct, fmtNum } from "../shared";

const PAGE_SIZE = 15;

// Heat ramp for absorbed_pct — higher absorption = warmer color (more demand pressure).
const absorbedTint = (pct) => {
  if (pct == null) return "text-gray-500 dark:text-gray-400";
  if (pct >= 50) return "text-red-600 dark:text-red-400 font-semibold";
  if (pct >= 25) return "text-amber-600 dark:text-amber-400 font-semibold";
  if (pct > 0)   return "text-emerald-600 dark:text-emerald-400 font-semibold";
  return "text-gray-500 dark:text-gray-400";
};

const AbsorptionVsBaselineTable = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No baseline comparison available yet." />;
  }
  return (
    <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
      <table className="w-full text-sm responsive-card-table">
        <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <th className="px-3 py-2.5 text-left font-semibold">Check-In</th>
            <th className="px-3 py-2.5 text-left font-semibold">Category</th>
            <th className="px-3 py-2.5 text-right font-semibold">Baseline Available</th>
            <th className="px-3 py-2.5 text-right font-semibold">Current Available</th>
            <th className="px-3 py-2.5 text-right font-semibold">Absorbed</th>
            <th className="px-3 py-2.5 text-right font-semibold">Absorbed %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.check_in}-${index}`} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
              <td data-label="Check-In" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtDate(row.check_in)}</td>
              <td data-label="Category" className="px-3 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">{categoryLabel(row.category_code)}</td>
              <td data-label="Baseline Available" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNum(row.baseline_inventory)}</td>
              <td data-label="Current Available" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNum(row.current_inventory)}</td>
              <td data-label="Absorbed" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNum(row.total_absorbed)}</td>
              <td data-label="Absorbed %" className={`px-3 py-2.5 text-right whitespace-nowrap ${absorbedTint(row.cumulative_absorption_pct)}`}>{fmtPct(row.cumulative_absorption_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ThinScroll>
  );
};

const AbsorptionVsBaselineTablePaginated = ({ rows }) => {
  const { page, setPage, pageItems, totalPages, totalItems } = useClientPagination(rows, { pageSize: PAGE_SIZE });
  return (
    <>
      <AbsorptionVsBaselineTable rows={pageItems} />
      {totalPages > 1 && (
        <div className="px-5 pt-1 pb-3 border-t border-gray-200 dark:border-gray-800">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={totalItems}
            perPage={PAGE_SIZE}
            label="rows"
          />
        </div>
      )}
    </>
  );
};

const AbsorptionVsBaselinePanel = ({ clientId, categoryCode, say }) => (
  <AnalysisPanel
    id={`absorption-vs-baseline-${clientId}-${categoryCode || "all"}`}
    title="Absorption vs Baseline"
    tip="Compares current availability against the first sync (the baseline) to show how much inventory has been booked since. Useful for spotting dates that are filling much faster than average."
    description="How much inventory has been absorbed since the first sync — current availability compared to the baseline. Requires 2+ sync cycles."
    badge={categoryCode ? categoryCode.toUpperCase() : null}
    fetcher={() => getCiReportAbsorptionVsBaseline(clientId, { categoryCode: categoryCode || "rv" })}
    deps={[clientId, categoryCode]}
  >
    {({ data }) => {
      if (!Array.isArray(data) && data?.status === "needs_multiple_syncs") {
        return <NeedsMultipleSyncs status={data.status} message={data.message} currentSync={data.current_sync} latestSyncDate={data.latest_sync_date} />;
      }
      const rows = Array.isArray(data) ? data : (data?.items ?? []);
      if (!rows.length) {
        return <NeedsMultipleSyncs status={data?.status} message={data?.message} currentSync={data?.current_sync} latestSyncDate={data?.latest_sync_date} />;
      }
      return (
        <>
          <PanelTakeaway panelKey="absorption_vs_baseline" say={say} />
          <AbsorptionVsBaselineTablePaginated rows={rows} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default AbsorptionVsBaselinePanel;
